<template>
    <div ref="root" class="relative shrink-0" @keydown="onKeydown">
        <button
            ref="trigger"
            type="button"
            dusk="AddOnsIconPicker"
            class="inline-flex h-11 items-center gap-1 rounded-lg border border-theme-border bg-theme-bg px-2 text-theme-text hover:border-theme-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent md:h-10"
            :aria-label="buttonLabel"
            :title="buttonLabel"
            aria-haspopup="menu"
            :aria-expanded="String(open)"
            @click="toggle"
        >
            <svg-icon :icon="addOnIcon(type)" width="18px" height="18px" />
            <svg-icon icon="chevron-down" width="12px" height="12px" />
        </button>
        <div
            v-if="open"
            role="menu"
            :aria-label="strings.iconMenuTitle"
            class="absolute right-0 z-20 mt-1 w-56 rounded-lg border border-theme-border bg-theme-bg p-1 shadow-[0_8px_24px_-8px_var(--theme-shadow-color)]"
        >
            <p
                aria-hidden="true"
                class="px-2 pb-1 pt-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-theme-toolbar-icon"
            >
                {{ strings.iconMenuTitle }}
            </p>
            <button
                ref="firstItem"
                type="button"
                role="menuitemradio"
                tabindex="-1"
                dusk="AddOnsIconAuto"
                :aria-checked="String(isChecked('auto'))"
                :class="itemClasses(isChecked('auto'))"
                @click="pick('auto')"
            >
                <svg-icon :icon="addOnIcon(inferredType)" width="16px" height="16px" />
                <span class="flex-1 text-left"
                    >{{ strings.iconAuto }} <em class="text-theme-toolbar-icon">{{ typeLabel(inferredType) }}</em></span
                >
                <svg-icon v-if="isChecked('auto')" icon="check" width="12px" height="12px" />
            </button>
            <span class="my-1 block h-px bg-theme-border" aria-hidden="true" />
            <button
                v-for="option in types"
                :key="option"
                type="button"
                role="menuitemradio"
                tabindex="-1"
                :dusk="`AddOnsIconOption-${option}`"
                :aria-checked="String(isChecked(option))"
                :class="itemClasses(isChecked(option))"
                @click="pick(option)"
            >
                <svg-icon :icon="addOnIcon(option)" width="16px" height="16px" />
                <span class="flex-1 text-left">{{ typeLabel(option) }}</span>
                <svg-icon v-if="isChecked(option)" icon="check" width="12px" height="12px" />
            </button>
        </div>
    </div>
</template>

<script>
'use strict';

import { mapState } from 'vuex';
import { addOnIcon } from './addOnIcon.js';
import { addOnStrings, addOnTypeLabel } from './addOnStrings.js';
import { ADD_ON_TYPES } from './addOnType';

const ITEM =
    'flex min-h-11 md:min-h-0 w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-theme-text hover:bg-theme-accent-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent';

export default {
    name: 'AddOnIconPicker',
    props: {
        type: { type: String, required: true },
        typeAuto: { type: Boolean, default: false },
        inferredType: { type: String, required: true },
    },
    emits: ['pick'],
    data() {
        return { open: false };
    },
    computed: {
        ...mapState(['localizations', 'lang']),
        strings() {
            return addOnStrings(this.localizations, this.lang);
        },
        types() {
            return ADD_ON_TYPES;
        },
        buttonLabel() {
            const label = `${this.strings.iconMenuTitle}: ${this.typeLabel(this.type)}`;
            return this.typeAuto ? `${label} · ${this.strings.iconAuto}` : label;
        },
    },
    mounted() {
        document.addEventListener('mousedown', this.closeOnInteractionOutside);
        document.addEventListener('focusin', this.closeOnInteractionOutside);
    },
    beforeDestroy() {
        document.removeEventListener('mousedown', this.closeOnInteractionOutside);
        document.removeEventListener('focusin', this.closeOnInteractionOutside);
    },
    methods: {
        addOnIcon,
        typeLabel(type) {
            return addOnTypeLabel(this.strings, type) || type;
        },
        // The colour, the glyph and what a reader announces all follow the
        // one answer, so no two options can disagree about
        // which is active.
        isChecked(option) {
            return option === 'auto' ? this.typeAuto : !this.typeAuto && this.type === option;
        },
        itemClasses(active) {
            return [ITEM, active ? 'text-theme-accent' : ''];
        },
        toggle() {
            this.open = !this.open;
            if (this.open) this.$nextTick(() => this.$refs.firstItem?.focus());
        },
        close() {
            this.open = false;
            this.$refs.trigger?.focus();
        },
        pick(type) {
            this.$emit('pick', type);
            this.close();
        },
        /**
         * An open menu owns these keys wherever focus sits in the picker: a
         * screen reader hands it the arrows instead of walking the
         * page, and the panel this menu opens over closes on
         * the Escape that would otherwise reach its trap
         * on the document.
         */
        onKeydown(event) {
            if (!this.open) return;

            const act = {
                Escape: () => this.close(),
                ArrowDown: () => this.moveFocus(1),
                ArrowUp: () => this.moveFocus(-1),
                Home: () => this.focusOption(0),
                End: () => this.focusOption(this.optionElements().length - 1),
            }[event.key];
            if (!act) return;

            event.preventDefault();
            event.stopPropagation();
            act();
        },
        moveFocus(step) {
            const options = this.optionElements();
            const from = options.indexOf(document.activeElement);
            this.focusOption(from < 0 ? 0 : (from + step + options.length) % options.length);
        },
        focusOption(index) {
            this.optionElements()[index]?.focus();
        },
        optionElements() {
            return Array.from(this.$refs.root.querySelectorAll('[role="menuitemradio"]'));
        },
        // A menu left open where the operator has moved on covers the form under it.
        closeOnInteractionOutside(event) {
            if (this.open && !this.$refs.root.contains(event.target)) this.open = false;
        },
    },
};
</script>
